/**
 * Nachtrag der Protokolluecke im Anlagedialog — die reinen Regeln.
 *
 * Aus InvoicesView herausgeloest: welche Zeilen eine Luecke haben, welche
 * davon eindeutig datierbar sind, welches Datum ein Nachtrag bekommt und was
 * der Nutzer danach liest. Zustand und Ablauf stehen in useBacklogDating.
 *
 * Getrennt, weil das Projekt keine DOM-Testumgebung hat: die Regeln lassen
 * sich nur ohne React pruefen, und an ihnen haengt, welches Datum im
 * Leistungsprotokoll landet.
 */
import { formatCents } from '../../services/money';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';

/**
 * Was der Nachtrag von einer Projektzeile des Anlagedialogs liest.
 *
 * Bewusst nur diese Felder: die Zeile traegt noch vieles mehr (Positionen,
 * Monate, Pauschalen), das hier keine Rolle spielt. Die Funktionen unten sind
 * generisch und geben die Zeilen des Aufrufers unveraendert zurueck — die
 * Liste darunter liest an ihnen weitere Felder.
 */
export interface BacklogRow {
    project: { id: string; name: string };
    /** Offene, aber undatierte Menge (undatedOpenSummary). */
    undated: { count: number; netCents: number };
    /** Die ganze Protokolluecke (undatedBacklog) — hier zaehlt nur, ob es sie gibt. */
    backlog: readonly unknown[];
    /** Erster protokollierter Tag bzw. Projektstart; '' heisst: kein Tag aus den Daten. */
    backlogSuggestedDate: string;
}

export interface UndatedTotal {
    count: number;
    netCents: number;
}

/** Summe der undatierten offenen Mengen ueber die angezeigten Projekte. */
export const sumUndated = (rows: readonly BacklogRow[]): UndatedTotal =>
    rows.reduce(
        (acc, r) => ({
            count: acc.count + r.undated.count,
            netCents: acc.netCents + r.undated.netCents,
        }),
        { count: 0, netCents: 0 },
    );

/**
 * Das Datum, das der Nachtrag dieses Projekts tatsaechlich bekommt.
 *
 * Reihenfolge: von Hand gesetzt, sonst der Vorschlag aus dem Protokoll,
 * sonst heute. Ein leeres Datum kommt nie durch — der Dienst weist es ab.
 */
export const chooseBacklogDate = (
    row: BacklogRow,
    manualDates: Readonly<Record<string, string>>,
    today: string,
): string => manualDates[row.project.id] || row.backlogSuggestedDate || today;

/** Projekte mit Mengen, die zu keinem Monat gehoeren. */
export const selectUndatedRows = <R extends BacklogRow>(rows: readonly R[]): R[] =>
    rows.filter((r) => r.backlog.length > 0);

/**
 * Eine Menge ohne Datum darf es nicht geben — sie wird datiert, nicht
 * gemeldet.
 *
 * DER TAG IST DER ERSTE PROTOKOLLIERTE TAG DES PROJEKTS, und das ist keine
 * Verlegenheitswahl, sondern die einzige, die kein Geld verschieben kann:
 *
 *   Eine Luecke heisst, dass Menge im Leistungsverzeichnis steht, von der
 *   nie ein Eintrag erzaehlt hat. Sie ist damit per Definition aelter als
 *   alles Protokollierte — sonst gaebe es den Eintrag.
 *
 *   Die Abrechnung teilt von ALT nach NEU zu (openByMonth: quantityBilled
 *   ist eine Summe ohne Zuordnung). Am Anfang eingesetzt, verbraucht die
 *   nachgetragene Menge deshalb genau den Ueberhang, den das bereits
 *   Abgerechnete heute in den datierten Eintraegen frisst — und was offen
 *   bleibt, ist wieder die JUENGSTE Leistung, mit ihrem echten Datum.
 *
 *   Nachgerechnet an 6051: 31 ohne Datum, 74 abgerechnet, datiert sind
 *   Maerz 1 + April 4 + Mai 12 + Juni 12 + Juli 14 + August 14 = 57. Heute
 *   frisst die Zuteilung alle 57 und der offene Rest haengt an keinem Tag.
 *   Mit den 31 am Anfang: 31 + 1 + 4 + 12 + 12 + 14 = 74, exakt das
 *   Abgerechnete — und offen bleiben die 14 datierten Tage im August. Das
 *   ist der Zeitraum, der auf die Rechnung gehoert.
 *
 * Voraussetzung bleibt ein Tag, der aus den Daten stammt: hat das Projekt
 * keinen einzigen datierten Eintrag und kein Startdatum, wird nichts
 * erfunden — die Zeile bleibt stehen und der Nutzer setzt den Tag.
 *
 * Was das Datum NICHT kann: den richtigen MONAT treffen, wenn die Luecke
 * doch junge Leistung ist (eine verlorene Buchung von gestern). Sie steht
 * dann im aeltesten Monat statt im richtigen. Verloren geht sie nicht —
 * abgerechnet wird von alt nach neu, sie kommt also zuerst dran. Der
 * Nutzer kann den Tag danach jederzeit richtigstellen.
 */
export const selectAutoDatableRows = <R extends BacklogRow>(undatedRows: readonly R[]): R[] =>
    undatedRows.filter((r) => Boolean(r.backlogSuggestedDate));

/** Die uebrigen — hier entscheidet der Nutzer. */
export const selectManualUndatedRows = <R extends BacklogRow>(
    undatedRows: readonly R[],
    autoDatableRows: readonly R[],
): R[] => undatedRows.filter((r) => !autoDatableRows.includes(r));

/**
 * Der Name, unter dem ein Nachtrag im Protokoll steht (performerName).
 *
 * Beide Nachtragswege bildeten ihn bisher je fuer sich, wortgleich — jetzt an
 * einer Stelle, damit sie nicht auseinanderlaufen. Bewusst unveraendert und
 * nicht ueber buildActorName: das waere eine Aenderung am Verhalten, keine
 * Auslagerung.
 */
export const backlogActorName = (
    profile: { first_name?: string | null; last_name?: string | null } | null | undefined,
): string => [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') || 'Administration';

/** Meldung nach dem Stapel der eindeutigen Faelle (handleDateObviousRows). */
export const obviousRowsNote = (
    count: number,
    netCents: number,
    failed: readonly string[],
): string =>
    count > 0
        ? `${count} Position(en) über ${formatCents(netCents)} wurden auf den ersten protokollierten Tag ihres Projekts datiert. Abgerechnet wird von alt nach neu — offen bleibt damit die jüngste Leistung mit ihrem echten Datum. Stimmt der Tag nicht, lässt er sich am Projekt richtigstellen.${
              failed.length
                  ? ` Nicht möglich bei: ${failed.join(', ')} — bitte von Hand.`
                  : ''
          }`
        : `Nichts nachzutragen.${failed.length ? ` Nicht möglich bei: ${failed.join(', ')}.` : ''}`;

/** Einzelner Nachtrag, aber das Protokoll war schon vollstaendig (count 0). */
export const nothingToDateNote = (projectName: string): string =>
    `Für „${projectName}" ist nichts nachzutragen — das Protokoll ist bereits vollständig.`;

/** Einzelner Nachtrag gespeichert. */
export const backlogDatedNote = (
    projectName: string,
    count: number,
    netCents: number,
    date: string,
): string =>
    `${projectName}: ${count} Position(en) über ${formatCents(netCents)} sind jetzt auf den ${date} datiert und fallen nicht mehr aus dem Zeitraum.`;

export const BACKLOG_SAVE_ERROR = 'Der Nachtrag konnte nicht gespeichert werden.';

/**
 * Die Meldung eines Fehlers, auch wenn er kein Error ist.
 *
 * Die Dienste reichen teils das rohe Fehlerobjekt der Datenbank durch — ein
 * schlichtes Objekt mit `message`, kein Error. Dessen Text soll weiter
 * erscheinen, wie zuvor beim Zugriff ueber `e?.message`.
 */
const messageOf = (e: unknown): string => {
    if (typeof e !== 'object' || e === null) return '';
    const message = (e as { message?: unknown }).message;
    return typeof message === 'string' ? message : '';
};

/**
 * Text fuer einen fehlgeschlagenen Nachtrag: der Rechtefehler im Klartext,
 * sonst die Meldung des Dienstes, sonst der allgemeine Satz.
 */
export const backlogSaveErrorText = (e: unknown): string =>
    rechtFehlerOder(e, () => messageOf(e) || BACKLOG_SAVE_ERROR);

export interface ProjectRefreshPorts<P> {
    /** Den geteilten Zwischenspeicher des Projekt-Loaders verwerfen. */
    invalidate: () => void;
    /** Den Projektbestand frisch holen. */
    load: () => Promise<P[]>;
    /** Den geholten Bestand in die Ansicht uebernehmen. */
    setProjects: (projects: P[]) => void;
}

/**
 * Den Projektbestand neu laden, nachdem am Leistungsprotokoll geschrieben
 * wurde (Nachtrag der Protokolluecke, Buchung einer Monatspauschale).
 *
 * Zuerst wird der Zwischenspeicher verworfen: der Projekt-Loader teilt
 * gleichzeitige Aufrufer auf einen laufenden Request, und ohne das Verwerfen
 * kaeme der Stand von VOR dem Schreiben zurueck (Begruendung an load() in
 * InvoicesView).
 *
 * Ein Fehlschlag beim Laden wird geschluckt und NIE weitergeworfen:
 * geschrieben ist zu diesem Zeitpunkt schon. Wuerfe der Helfer, landete der
 * Fehler beim Aufrufer im Zweig "nicht gespeichert". Genau das tat der
 * einzelne Nachtrag bisher: ein bloss fehlgeschlagenes Neuladen meldete dort
 * einen gescheiterten Nachtrag, obwohl er laengst im Protokoll stand.
 */
export const refreshProjectList = async <P>(ports: ProjectRefreshPorts<P>): Promise<void> => {
    ports.invalidate();
    try {
        ports.setProjects(await ports.load());
    } catch {
        /* die Liste bleibt auf dem Stand von vorhin */
    }
};
